import React from "react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/inquiry";
import { fmtDate } from "@/lib/format";
import { Paperclip, Link2, Settings2, CornerDownRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const download = async (a) => {
  const r = await api.get(`/inquiry-attachments/${a.id}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const el = document.createElement("a"); el.href = url; el.download = a.filename; el.click(); URL.revokeObjectURL(url);
};

const Meta = ({ e }) => (
  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500 mt-1">
    {e.channel && <span>via <b className="text-slate-700">{e.channel}</b></span>}
    {e.user_name && <span>by <b className="text-slate-700">{e.user_name}</b></span>}
    {e.contact_names?.length > 0 && <span>with {e.contact_names.join(", ")}</span>}
    {e.created_by !== e.user_id && e.created_by_name && <span>recorded by {e.created_by_name}</span>}
  </div>
);

const Entry = ({ e, onCorrect, onLink }) => (
  <li className="relative pl-8 pb-6" data-testid={`ev-${e.id}`}>
    <span className={`absolute left-0 top-1 w-5 h-5 rounded-full flex items-center justify-center border ${e.system ? "bg-slate-100 border-slate-300" : "bg-blue-700 border-blue-700"}`}>
      {e.system ? <Settings2 className="w-3 h-3 text-slate-500" /> : <span className="w-1.5 h-1.5 rounded-full bg-white" />}
    </span>
    <div className="flex flex-wrap items-baseline gap-2">
      <span className="mono text-xs text-slate-500">{fmtDateTime(e.at)}</span>
      <span className={`text-sm font-semibold ${e.system ? "text-slate-600" : "text-slate-900"}`}>{e.type}</span>
      {e.outcome && <span className="text-xs px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-800 border border-blue-100">{e.outcome}</span>}
      {e.direction && !e.system && <span data-testid={`ev-dir-tag-${e.id}`} className="text-[11px] text-slate-500">{{ incoming: "← from contact", outgoing: "→ to contact", internal: "internal" }[e.direction]}</span>}
      {e.corrects_event_id && <span className="text-[11px] text-amber-700 inline-flex items-center gap-1"><CornerDownRight className="w-3 h-3" />correction</span>}
    </div>
    {e.notes && <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{e.notes}</div>}
    {(e.next_action || e.followup_date) && (
      <div className="text-xs text-slate-600 mt-1">Next: <b>{e.next_action || "follow up"}</b>{e.followup_date && <> on <b>{fmtDate(e.followup_date)}</b></>}</div>
    )}
    {e.link && <button type="button" data-testid={`ev-link-${e.id}`} onClick={() => onLink(e.link)} className="text-xs text-blue-700 inline-flex items-center gap-1 mt-1 hover:underline"><Link2 className="w-3 h-3" />Open {e.link.kind}: {e.link.label}</button>}
    {e.attachments?.length > 0 && (
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {e.attachments.map((a) => <button key={a.id} type="button" data-testid={`ev-att-${a.id}`} onClick={() => download(a)} className="text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-slate-200 hover:border-blue-300 hover:text-blue-700"><Paperclip className="w-3 h-3" />{a.filename}</button>)}
      </div>
    )}
    <Meta e={e} />
    {!e.system && <button type="button" data-testid={`ev-correct-${e.id}`} onClick={() => onCorrect(e)} className="text-[11px] text-slate-400 hover:text-blue-700 mt-1">Add correction</button>}
  </li>
);

export const JourneyTimeline = ({ events, onCorrect }) => {
  const nav = useNavigate();
  const openLink = (l) => nav(l.kind === "task" ? `/tasks/${l.id}` : `/obligations?q=${encodeURIComponent(l.label)}`);
  if (!events.length) return <div className="text-sm text-slate-500" data-testid="journey-empty">No journey entries yet.</div>;
  return (
    <ol className="relative border-l border-blue-100 ml-2.5" data-testid="journey-timeline">
      <div className="-ml-2.5">{events.map((e) => <Entry key={e.id} e={e} onCorrect={onCorrect} onLink={openLink} />)}</div>
    </ol>
  );
};
